import { useState } from "react";
import { __ } from "@wordpress/i18n";
import SubSection from "./SubSection";
import UpgradeToPro from "./UpgradeToPro";
import Astra_Admin_Icons from "@Common/block-icons";
import SearchResults from "./SearchResults";
import { Link } from "react-router-dom";
import { PromoCard } from "@astra-components";
import { useDebounceEffect } from "@astra-hooks";

const Docs = ({ setOpen }) => {
	const [searchKeyword, setSearchKeyword] = useState("");
	const [searchResults, setSearchResults] = useState(null);

	// Debounced filtering of docs based on the search keyword, updating results only after typing pauses.
	useDebounceEffect(
		() => {
			if (searchKeyword === "") {
				setSearchResults(null);
			} else {
				const data = astra_admin.astra_docs_data.docs.filter((item) =>
					item.title.toLowerCase().includes(searchKeyword)
				);
				setSearchResults(data);
			}
		},
		300,
		[searchKeyword]
	);

	const is404 = astra_admin.astra_docs_data?.data?.status === 404;

	return (
		<main className="bg-white">
			<div className="">
				<h1 className="sr-only"> {__("Welcome to KB Docs", "astra")} </h1>
				<div className="w-full flex justify-between items-center bg-gray-50 py-5 px-6">
					<a
						href={astra_admin.astra_base_url}
						className="flex-shrink-0 flex items-center ast-box-shadow-none"
					>
						<img
							className="lg:block mr-3 h-[2.6rem] w-auto ast-box-shadow-none"
							src={astra_admin.logo_url}
							alt="Workflow"
						/>
					</a>
					<div className="max-w-5xl mx-auto w-full relative">
						<input
							type="search"
							placeholder={__("Search", "astra")}
							className="w-full ast-docs-search-fields text-base"
							onChange={(e) => setSearchKeyword(e.target.value)}
						/>
						<div className="absolute top-1/2 -translate-y-1/2 left-4">
							{searchKeyword ? (
								Astra_Admin_Icons['close']
							) : (
								Astra_Admin_Icons['search']
							)}
						</div>
					</div>
					{setOpen && (
						<div>
							<button onClick={() => setOpen(false)}>
								{Astra_Admin_Icons["popupclose"]}
							</button>
						</div>
					)}
				</div>

				<div className="sm:max-w-5xl mx-auto w-full flex flex-col">
					<div className="flex flex-col lg:flex-row gap-8 pt-10">
						<div className="w-full px-6 lg:px-0 lg:w-2/3 mb-5 self-center">
							{is404 && (
								<div className="flex items-center justify-center">
									<div className="text-center">
										<h2 className="text-4xl font-bold text-gray-800 mb-4">
											{__("404", "astra")}
										</h2>
										<p className="text-xl text-gray-600 mb-8">
											{__(
												"Oops! Documentation not found.",
												"astra"
											)}
										</p>
										<p className="text-lg text-gray-600 mb-8">
											{
												astra_admin.astra_docs_data
													?.message
											}
										</p>
										<Link
											role="button"
											to={astra_admin.astra_base_url}
											className="sm:inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-astra focus-visible:bg-astra-hover hover:text-white hover:bg-astra-hover focus:outline-none mr-4 mb-2 sm:mb-0"
										>
											{__("Go to Dashboard", "astra")}
										</Link>
									</div>
								</div>
							)}

							{!is404 && (
								<>
									{searchResults && searchResults.length > 0 && (
										<div className="mb-8">
											<SearchResults
												data={searchResults}
											/>
										</div>
									)}
									{/* Docs subsections */}
									{Object.entries(
										astra_admin.astra_docs_data.categories
									).map((item, key) => (
										<SubSection item={item} key={key} />
									))}
									{!astra_admin.pro_available &&
										astra_admin.upgrade_notice && (
											<div className="mt-10">
												<UpgradeToPro />
											</div>
										)}
								</>
							)}
						</div>
						<div className="w-full px-6 lg:px-0 lg:w-1/3">
							<PromoCard
								id="vip-priority-support"
								className="border border-sky-500 mb-5"
								icon={Astra_Admin_Icons["support"]}
								title={
									<span className="font-medium">
										{__("VIP Priority Support", "astra")}
									</span>
								}
								description={__(
									"Faster and exclusive support service designed for VIP assistance and benefits.",
									"astra"
								)}
								linkHRef={ astra_admin.astraWebsite?.vipPrioritySupportUrl }
								linkText={__("Know More →", "astra")}
							/>

							<PromoCard
								id="view-documentation"
								className="border border-slate-200 mb-5"
								icon={Astra_Admin_Icons["book"]}
								title={
									<span className="font-medium">
										{__("View Documentation", "astra")}
									</span>
								}
								description={__(
									`Browse documentation, reference material, and tutorials for ${astra_admin.theme_name} Theme.`,
									"astra"
								)}
								linkHRef={ astra_admin.astraWebsite?.docsUrl }
								linkText={
									<span className="inline-flex items-center gap-2">
										{__("View All Documentation", "astra")}
										{Astra_Admin_Icons["redirect"]}
									</span>
								}
							/>
						</div>
					</div>
				</div>
			</div>
		</main>
	);
};

export default Docs;
